import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, ShieldAlert, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Owner-only permanent purge. Reused by Customers, Service Locations, Personnel
// and Assignments. Runs the server-side dependency preflight first; only offers
// execution when no blocking operational history exists.
export const OwnerPurgeDialog = ({ entityType, entityId, onClose, onPurged }) => {
  const { token } = useContext(AuthContext);
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [pre, setPre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [finalConfirm, setFinalConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadPreflight = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/purge/preflight`, { entity_type: entityType, entity_id: entityId }, H);
      setPre(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run dependency preflight');
      onClose();
    } finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { loadPreflight(); }, [loadPreflight]);

  const norm = (v) => (v || '').trim().toLowerCase();
  const canExecute = pre?.can_purge
    && norm(confirmText) === norm(pre?.owner_email || pre?.confirm_target)
    && reason.trim().length >= 3
    && finalConfirm;

  const execute = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/purge/execute`,
        { entity_type: entityType, entity_id: entityId, confirm_text: confirmText, reason, final_confirm: finalConfirm }, H);
      toast.success(r.data?.message || 'Record permanently purged');
      onPurged?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Purge failed'));
    } finally { setBusy(false); }
  };

  const Row = ({ c, tone }) => (
    <li className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
      data-testid={`purge-dep-${c.collection}`}
      style={tone === 'block'
        ? { borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c' }
        : { borderColor: '#e2e8f0', background: '#f8fafc', color: '#475569' }}>
      <span>{c.label}</span>
      <span className="font-semibold">{c.count}</span>
    </li>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="owner-purge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert size={20} /> Owner Permanent Purge
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-10 text-slate-500"><Loader2 className="animate-spin" size={18} /> Running dependency preflight…</div>
        ) : pre && (
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <p><b>Purge is permanent and cannot be undone.</b> This is only for test, duplicate, erroneous or corrupted records. For normal removals, use <b>Deactivate</b> instead.</p>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{pre.entity_label}</div>
              <div className="text-base font-bold text-[#0E2A47]" data-testid="purge-record-name">{pre.record_name}</div>
            </div>

            {pre.blocking?.length > 0 && (
              <div data-testid="purge-blocking">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-600">Blocking — cannot purge</div>
                <ul className="space-y-1">{pre.blocking.map((c) => <Row key={c.collection} c={c} tone="block" />)}</ul>
                <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">{pre.blocking_message}</p>
              </div>
            )}

            {pre.cascade?.length > 0 && (
              <div data-testid="purge-cascade">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Will also be permanently removed</div>
                <ul className="space-y-1">{pre.cascade.map((c) => <Row key={c.collection} c={c} tone="cascade" />)}</ul>
              </div>
            )}

            {pre.can_purge && (
              <>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Type your Owner email <b className="text-slate-700">{pre.owner_email || pre.confirm_target}</b> to authorize
                  </span>
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                    data-testid="purge-confirm-input" type="email" autoComplete="off"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" placeholder="you@tfsi-us.com" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Purge reason <span className="text-red-500">*</span></span>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    data-testid="purge-reason-input"
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" placeholder="Why is this record being permanently purged?" />
                </label>
                <label className="flex items-start gap-2 text-sm text-red-700" data-testid="purge-final-confirm">
                  <input type="checkbox" checked={finalConfirm} onChange={(e) => setFinalConfirm(e.target.checked)} className="mt-0.5" />
                  <span>I understand this permanently deletes <b>{pre.record_name}</b> and its listed structural records, and cannot be undone.</span>
                </label>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm" data-testid="purge-cancel">Cancel</button>
          {pre?.can_purge && (
            <button onClick={execute} disabled={!canExecute || busy} data-testid="purge-execute-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <ShieldAlert size={15} />} Permanently Purge
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OwnerPurgeDialog;
